import ThumbnailCard from "@components/Blocks/Card/Thumbnail/ThumbnailCard";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import SubSectionNav from "@components/Blocks/SubSectionNav/SubSectionNav";
import LoadMore from "@components/Modules/LoadMore/LoadMore";
import { CompanionAdsImu1 } from "@elements/Advertisement/variant/CompanionAdImu";
import VideoPlayerSticky from "@elements/Player/VideoPlayerSticky";
import { SectionTitleV2 } from "@elements/Typography/TitleV2";
import { displayAfter, isArrayWithElements } from "@helper/utils";
import useWindowDimensions from "@hooks/useWindowDimensions";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import { NoSSR } from "@sphtech/web2-core/components";
import { PageAdTargetValue } from "@typings/Ads.d";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import type { BrightcovePlayer } from "@typings/Brightcove.d";
import type { ReactNode } from "react";
import React from "react";

import ShortFormVideoBlock from "./Components/ShortFormVideoBlock";

type LandscapeVideoPageProps = {
  playing: LatestVideoResponse | undefined;
  containerRef: React.RefObject<HTMLDivElement | null>;
  videos: LatestVideoResponse[] | undefined;
  sectionName: string;
  pageAdTargetValue: PageAdTargetValue;
  handleOnEndedVideo: () => void;
  handleOnClickThumbnail: (props: LatestVideoResponse) => void;
  handleLoadMoreVideo: () => Promise<number>;
  shortFormVideos: LatestVideoResponse[];
  handlePlayerReady?: (player: BrightcovePlayer) => void;
};

const PAGE_SIZE = 4;

export default function LandscapeVideoPage(props: LandscapeVideoPageProps) {
  const {
    playing,
    containerRef,
    videos,
    sectionName,
    pageAdTargetValue,
    handleOnEndedVideo,
    handleOnClickThumbnail,
    handleLoadMoreVideo,
    shortFormVideos,
    handlePlayerReady,
  } = props;

  const displaySectionName = "Multimedia";
  const sectionHeading = `Videos - ${displaySectionName}`;

  const { width } = useWindowDimensions();
  // Tailwind's `lg` breakpoint is 992px by default — consider that desktop
  const isDesktop = width >= 992;

  return (
    <>
      <WidgetWrapper className="lg:px-0">
        <SectionTitleV2
          sectionName={displaySectionName}
          sectionHeading={sectionHeading}
        />
        <NoSSR>
          <SubSectionNav
            sectionName={sectionName}
            displaySection={displaySectionName}
          />
        </NoSSR>

        {!isDesktop && playing?.id && (
          <VideoPlayerSticky
            playing={playing}
            forwardedRef={containerRef}
            onEnded={handleOnEndedVideo}
            nextPlaylist={handleOnEndedVideo}
            showDescription={true}
            variant="landscape_video_page"
            onPlayerReady={handlePlayerReady}
          />
        )}

        <div className="flex flex-col lg:flex-row gap-5 mt-5">
          <div className="hidden lg:block lg:w-13/20 flex-grow-no-shrink">
            {isDesktop && playing?.id && (
              <VideoPlayerSticky
                playing={playing}
                forwardedRef={containerRef}
                onEnded={handleOnEndedVideo}
                nextPlaylist={handleOnEndedVideo}
                showDescription={true}
                variant="landscape_video_page"
                onPlayerReady={handlePlayerReady}
              />
            )}
          </div>
          <div className="flex flex-col gap-4 lg:gap-5 w-full lg:max-w-[397px]">
            {isArrayWithElements(videos) &&
              videos.reduce((acc: ReactNode[], _, index) => {
                // Insert an ad every PAGE_SIZE articles, outside the wrapper
                if (displayAfter(index, PAGE_SIZE)) {
                  acc.push(
                    <React.Fragment key={`scroll-elem-${index}`}>
                      <AdsContainer>
                        <CompanionAdsImu1
                          uniqueSectionName={sectionName}
                          index={index / PAGE_SIZE}
                          pageAdTargetType={pageAdTargetValue}
                          applySizeMapping={true}
                        />
                      </AdsContainer>
                      <ShortFormVideoBlock videos={shortFormVideos} />
                    </React.Fragment>,
                  );
                }
                // Start a new batch every 8 articles
                if (index % PAGE_SIZE === 0) {
                  acc.push(
                    <div key={`group-${index}`} className="flex flex-col gap-5">
                      {videos
                        .slice(index, index + PAGE_SIZE)
                        .map((video, idx) => (
                          <ThumbnailCard<LatestVideoResponse>
                            key={`video-${index}-${idx}`}
                            handleOnClickThumbnail={handleOnClickThumbnail}
                            variant="landscape_video_page"
                            {...video}
                          />
                        ))}
                    </div>,
                  );
                }

                return acc;
              }, [])}
            <div className="my-md">
              <LoadMore
                onLoadMore={handleLoadMoreVideo}
                loadText="Laporan lain videos"
                virtualPVTitle="Videos"
                pageNumber={0}
              />
            </div>
          </div>
        </div>
      </WidgetWrapper>
    </>
  );
}
